/**
 * Hand-rolled Web Audio engine (DESIGN-client §8). Signal graph:
 *
 *   sfx bus ───────────────────────────┐
 *   music bus ─▶ music duck ───────────┼─▶ master ─▶ destination
 *   announcer bus ─▶ announcer duck ───┘
 *
 * Bus nodes carry the user volume; duck nodes carry voice ducking (sfx is never ducked), so the
 * two never fight over one AudioParam. Ramps use cancelScheduledValues → setValueAtTime(current)
 * → linearRampToValueAtTime so an in-flight ramp is picked up smoothly instead of snapping.
 * Unlock / auto-resume patterns re-implemented from Ringmaster's stagePlayer (not imported).
 */
import { recordPlayed, type PlayedRecord } from '../scene/testApi';

export type Bus = 'sfx' | 'music' | 'announcer';
export type DuckedBus = 'music' | 'announcer';
export type VolumeTarget = Bus | 'master';
export type Platform = 'discord' | 'web';

export interface PlayOpts {
  bus?: Bus;
  gain?: number;
  /** playbackRate multiplier (1 = native). */
  pitch?: number;
  delayMs?: number;
}

/** `id → [startSec, durationSec]` inside a sprite buffer. */
export type SpriteMap = Record<string, [start: number, dur: number]>;

export interface DuckConfig {
  music: number;
  announcer: number;
  attackMs: number;
  releaseMs: number;
}

export const DEFAULT_DUCK: Readonly<DuckConfig> = Object.freeze({ music: 0.3, announcer: 0.5, attackMs: 150, releaseMs: 600 });

/**
 * Engine-side fallbacks, used only until the store pushes the player's own levels in.
 *
 * Discord's music was 0 while there was no music to play. It is 0.35 now that there is: a bed
 * under a voice call rather than a soundtrack, and the announcer/voice duck still pulls it down
 * whenever anyone speaks. `store.DEFAULT_MUSIC_VOLUME` holds the same two numbers for the slider
 * a player actually sees; these two tables must not drift apart.
 */
export const DEFAULT_VOLUMES: Record<Platform, Record<VolumeTarget, number>> = {
  discord: { master: 0.6, sfx: 1, music: 0.35, announcer: 1 },
  web: { master: 0.8, sfx: 1, music: 0.5, announcer: 1 },
};

export const VOLUME_RAMP_MS = 80;
export const WEBM_MIME = 'audio/webm; codecs="opus"';

export interface AudioEngineOptions {
  platform?: Platform;
  /** Injected for tests (a fake AudioContext). Return null when Web Audio is unavailable. */
  createContext?: () => AudioContext | null;
  /** Format probe; defaults to `HTMLAudioElement.canPlayType`. */
  canPlay?: (mime: string) => boolean;
  fetchBuffer?: (url: string) => Promise<ArrayBuffer>;
  /** Every `play` call is reported here (default: `window.__spades.audio.lastPlayed` under e2e). */
  onPlayed?: (rec: PlayedRecord) => void;
  now?: () => number;
}

interface Sample {
  buffer: AudioBuffer;
  start: number;
  dur: number;
}

function defaultCreateContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const Ctor =
    window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  return Ctor ? new Ctor() : null;
}

function defaultCanPlay(mime: string): boolean {
  if (typeof document === 'undefined') return false;
  try {
    return document.createElement('audio').canPlayType(mime) !== '';
  } catch {
    return false;
  }
}

async function defaultFetch(url: string): Promise<ArrayBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`audio fetch ${url}: ${res.status}`);
  return res.arrayBuffer();
}

/**
 * Smooth ramp on an AudioParam: pick up the current value, then linear-ramp to `target`.
 * The value is read BEFORE cancelScheduledValues: per spec, cancelling reverts the computed
 * value to the last event, which would snap an in-flight ramp.
 */
export function rampParam(param: AudioParam, target: number, now: number, ms: number): void {
  try {
    const current = param.value;
    param.cancelScheduledValues(now);
    param.setValueAtTime(current, now);
    param.linearRampToValueAtTime(target, now + Math.max(0, ms) / 1000);
  } catch {
    /* detached node — ignore */
  }
}

export class AudioEngine {
  ctx: AudioContext | null = null;
  master: GainNode | null = null;
  bus: Record<Bus, GainNode> | null = null;
  duckNode: Record<DuckedBus, GainNode> | null = null;
  duck: DuckConfig = { ...DEFAULT_DUCK };
  readonly speakers = new Set<string>();
  readonly platform: Platform;
  readonly volumes: Record<VolumeTarget, number>;
  /**
   * Master mute. Kept apart from `volumes.master` so unmuting restores the level the player had,
   * and so a slider dragged while muted still records where it was dragged to.
   */
  private mutedFlag = false;

  private unlocked = false;
  private autoResumeWired = false;
  private readonly unlockListeners = new Set<() => void>();
  private readonly samples = new Map<string, Sample>();
  private readonly sprites = new Map<string, { buffer: AudioBuffer; map: SpriteMap }>();
  private readonly createContext: () => AudioContext | null;
  private readonly canPlay: (mime: string) => boolean;
  private readonly fetchBuffer: (url: string) => Promise<ArrayBuffer>;
  private readonly onPlayed: (rec: PlayedRecord) => void;
  private readonly now: () => number;

  constructor(opts: AudioEngineOptions = {}) {
    this.platform = opts.platform ?? 'web';
    this.volumes = { ...DEFAULT_VOLUMES[this.platform] };
    this.createContext = opts.createContext ?? defaultCreateContext;
    this.canPlay = opts.canPlay ?? defaultCanPlay;
    this.fetchBuffer = opts.fetchBuffer ?? defaultFetch;
    this.onPlayed = opts.onPlayed ?? recordPlayed;
    this.now = opts.now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
  }

  get isUnlocked(): boolean {
    return this.unlocked;
  }

  get isSpeaking(): boolean {
    return this.speakers.size > 0;
  }

  // ── context / graph ───────────────────────────────────────────────────────

  /** Lazily create the context and the bus graph. Returns null when Web Audio is unavailable. */
  ensureContext(): AudioContext | null {
    if (this.ctx) return this.ctx;
    let ctx: AudioContext | null = null;
    try {
      ctx = this.createContext();
    } catch {
      ctx = null;
    }
    if (!ctx) return null;
    this.ctx = ctx;
    // A context can reach `running` long after the gesture that created it: autoplay policy keeps
    // it suspended, `tryUnlock`'s settle sees `suspended` and gives up, and a later `resume()` from
    // the pointer handler flips the state with nobody watching. Latching on `statechange` is what
    // makes the difference between audio and a silent session (client review #1/#29).
    if (typeof ctx.addEventListener === 'function') {
      ctx.addEventListener('statechange', () => {
        if (this.ctx?.state === 'running') this.markUnlocked();
      });
    }
    const master = ctx.createGain();
    master.gain.value = this.masterGain();
    master.connect(ctx.destination);
    this.master = master;

    const mkBus = (name: Bus): GainNode => {
      const g = ctx.createGain();
      g.gain.value = this.volumes[name];
      return g;
    };
    const sfx = mkBus('sfx');
    const music = mkBus('music');
    const announcer = mkBus('announcer');
    this.bus = { sfx, music, announcer };

    const ducking = this.speakers.size > 0;
    const mkDuck = (level: number): GainNode => {
      const g = ctx.createGain();
      g.gain.value = ducking ? level : 1;
      g.connect(master);
      return g;
    };
    const musicDuck = mkDuck(this.duck.music);
    const announcerDuck = mkDuck(this.duck.announcer);
    this.duckNode = { music: musicDuck, announcer: announcerDuck };

    sfx.connect(master);
    music.connect(musicDuck);
    announcer.connect(announcerDuck);
    return ctx;
  }

  private markUnlocked(): void {
    if (this.unlocked) return;
    this.unlocked = true;
    const waiting = [...this.unlockListeners];
    this.unlockListeners.clear();
    for (const cb of waiting) {
      try {
        cb();
      } catch {
        /* a listener throwing must not break audio */
      }
    }
  }

  /** Call inside a user gesture (Boot "Deal me in" tap): plays a silent frame and resumes. */
  unlock(): void {
    if (this.unlocked) return;
    const ctx = this.ensureContext();
    if (!ctx) return;
    try {
      const buf = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      src.start(0);
    } catch {
      /* silent */
    }
    ctx.resume().catch(() => {
      /* silent */
    });
    this.ensureAutoResume();
    this.markUnlocked();
  }

  /** Unlock without a gesture (Discord launch click usually carries one through). Never throws. */
  tryUnlock(): void {
    if (this.unlocked) return;
    const ctx = this.ensureContext();
    if (!ctx) return;
    this.ensureAutoResume();
    const settle = (): void => {
      if (this.ctx?.state === 'running') this.markUnlocked();
    };
    try {
      ctx.resume().then(settle, settle);
    } catch {
      /* silent */
    }
    settle();
  }

  /** Fires once (immediately if already unlocked). */
  onUnlocked(cb: () => void): () => void {
    if (this.unlocked) {
      cb();
      return () => {};
    }
    this.unlockListeners.add(cb);
    return () => {
      this.unlockListeners.delete(cb);
    };
  }

  /** Resume a browser-suspended context on return-to-visible, focus, and the next pointer gesture. */
  ensureAutoResume(): void {
    if (this.autoResumeWired || typeof document === 'undefined') return;
    this.autoResumeWired = true;
    const tryResume = (): void => {
      const ctx = this.ctx;
      if (!ctx) return;
      const state = ctx.state as string;
      if (state === 'running') {
        this.markUnlocked();
        return;
      }
      if (state === 'suspended' || state === 'interrupted') {
        const settle = (): void => {
          if (this.ctx?.state === 'running') this.markUnlocked();
        };
        ctx.resume().then(settle, settle);
      }
    };
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) tryResume();
    });
    window.addEventListener('focus', tryResume);
    // Not `{ once: true }`: the first gesture may be consumed by a policy that still refuses to
    // resume, and a session that stays silent because we stopped asking is the worst outcome.
    // The handler is cheap and no-ops once the latch is set.
    document.addEventListener('pointerdown', tryResume, { capture: true, passive: true });
  }

  // ── samples ───────────────────────────────────────────────────────────────

  /** Picks webm/opus when playable, else mp3; fetches, decodes and registers every sprite id. */
  async loadSprite(name: string, src: { webm: string; mp3: string }, map: SpriteMap): Promise<void> {
    const ctx = this.ensureContext();
    if (!ctx) return;
    const url = this.canPlay(WEBM_MIME) ? src.webm : src.mp3;
    const bytes = await this.fetchBuffer(url);
    const buffer = await ctx.decodeAudioData(bytes);
    this.registerSprite(name, buffer, map);
  }

  /** Register an already-decoded buffer (tests, generated tones). */
  registerSprite(name: string, buffer: AudioBuffer, map: SpriteMap): void {
    this.sprites.set(name, { buffer, map });
    for (const [id, [start, dur]] of Object.entries(map)) this.samples.set(id, { buffer, start, dur });
  }

  /**
   * Register one decoded buffer as a whole sample (announcer clips, which are individual files
   * rather than windows into a sprite).
   */
  registerSample(id: string, buffer: AudioBuffer): void {
    this.samples.set(id, { buffer, start: 0, dur: buffer.duration });
  }

  /**
   * Fetch and decode a single file under `id`. Resolves false when Web Audio is unavailable or
   * the file cannot be fetched/decoded; a missing announcer clip costs subtitles-only, never the
   * game, so this never throws.
   */
  async loadClip(id: string, url: string): Promise<boolean> {
    if (this.samples.has(id)) return true;
    const ctx = this.ensureContext();
    if (!ctx) return false;
    try {
      const bytes = await this.fetchBuffer(url);
      const buffer = await ctx.decodeAudioData(bytes);
      this.registerSample(id, buffer);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Public view of the injected format probe.
   *
   * `loadSprite` picks webm-vs-mp3 with it and so does the music director, which streams through
   * an `HTMLAudioElement` instead of decoding — one probe, one answer, and a test that injects
   * `canPlay` steers both.
   */
  canPlayMime(mime: string): boolean {
    return this.canPlay(mime);
  }

  has(id: string): boolean {
    return this.samples.has(id);
  }

  sampleIds(): string[] {
    return [...this.samples.keys()];
  }

  /**
   * One-shot playback. Pitch via `playbackRate`; sprite window via `start(when, offset, dur)`.
   * Always reported to `onPlayed` (with `audible:false` when locked or unknown) so tests can
   * assert the mix even in a silent environment.
   */
  play(id: string, opts: PlayOpts = {}): AudioBufferSourceNode | null {
    const bus = opts.bus ?? 'sfx';
    const gain = opts.gain ?? 1;
    const pitch = opts.pitch ?? 1;
    const delayMs = opts.delayMs ?? 0;
    const sample = this.samples.get(id);
    const ctx = this.ctx;
    const audible = !!sample && !!ctx && !!this.bus && this.unlocked;
    this.onPlayed({ id, bus, gain, pitch, delayMs, at: this.now(), audible });
    if (!audible || !sample || !ctx || !this.bus) return null;
    try {
      const src = ctx.createBufferSource();
      src.buffer = sample.buffer;
      src.playbackRate.value = pitch;
      const g = ctx.createGain();
      g.gain.value = gain;
      src.connect(g);
      g.connect(this.bus[bus]);
      src.onended = () => {
        try {
          src.disconnect();
          g.disconnect();
        } catch {
          /* already gone */
        }
      };
      src.start(ctx.currentTime + Math.max(0, delayMs) / 1000, sample.start, sample.dur);
      return src;
    } catch {
      return null;
    }
  }

  // ── ducking / volume ──────────────────────────────────────────────────────

  setSpeaking(userId: string, on: boolean): void {
    const was = this.speakers.size > 0;
    if (on) this.speakers.add(userId);
    else this.speakers.delete(userId);
    const now = this.speakers.size > 0;
    if (was !== now) this.applyDuck(now);
  }

  applyDuck(active: boolean): void {
    const ctx = this.ctx;
    if (!ctx || !this.duckNode) return;
    const now = ctx.currentTime;
    const ms = active ? this.duck.attackMs : this.duck.releaseMs;
    rampParam(this.duckNode.music.gain, active ? this.duck.music : 1, now, ms);
    rampParam(this.duckNode.announcer.gain, active ? this.duck.announcer : 1, now, ms);
  }

  /** 80ms ramp. Remembered even before the context exists. */
  setVolume(which: VolumeTarget, v: number): void {
    const value = Math.min(1, Math.max(0, v));
    this.volumes[which] = value;
    const ctx = this.ctx;
    if (!ctx) return;
    const node = which === 'master' ? this.master : this.bus?.[which];
    if (node) rampParam(node.gain, which === 'master' ? this.masterGain() : value, ctx.currentTime, VOLUME_RAMP_MS);
  }

  getVolume(which: VolumeTarget): number {
    return this.volumes[which];
  }

  /** Master mute: the whole graph to silence on the same 80ms ramp, `volumes.master` untouched. */
  setMuted(muted: boolean): void {
    if (this.mutedFlag === muted) return;
    this.mutedFlag = muted;
    const ctx = this.ctx;
    if (ctx && this.master) rampParam(this.master.gain, this.masterGain(), ctx.currentTime, VOLUME_RAMP_MS);
  }

  isMuted(): boolean {
    return this.mutedFlag;
  }

  /** What the master node should sit at: the user's level, or nothing while muted. */
  private masterGain(): number {
    return this.mutedFlag ? 0 : this.volumes.master;
  }

  /** Live duck multiplier for a ducked bus (1 when not ducking or no context). */
  duckGain(bus: DuckedBus): number {
    return this.duckNode?.[bus].gain.value ?? (this.speakers.size > 0 ? this.duck[bus] : 1);
  }

  /** Effective bus gain right now: user volume × duck. */
  busGain(bus: Bus): number {
    const vol = this.bus?.[bus].gain.value ?? this.volumes[bus];
    return bus === 'sfx' ? vol : vol * this.duckGain(bus);
  }
}

let singleton: AudioEngine | null = null;

/** Process-wide engine; `opts` only apply to the first call. */
export function getAudioEngine(opts?: AudioEngineOptions): AudioEngine {
  singleton ??= new AudioEngine(opts);
  return singleton;
}

/** Tests only. */
export function resetAudioEngine(): void {
  singleton = null;
}
